import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { toast } from 'utils/toast';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import EditColorSidebar from './EditColorSidebar';
import { PreviewArea } from 'components/wysiwyg/PreviewArea';
import { RenderRequest, Variant, DataScenario } from 'types/cx-editor';
import { ColorPaletteAPI, SaveColorParams, ImpactTemplate, ColorImpactResponse } from 'apis/colorPaletteAPI';

const DEFAULT_VARIANT: Variant = { hierarchy: 'Project', locale: 'en_US', scenario: 'default' };
const DEFAULT_LOCALES = ['en_US'];

// Helper to parse standardColorId into category id and shade
// e.g., "primary_200" -> { id: "primary", shade: "200" }
// e.g., "data_colors_graph_primary" -> { id: "data_colors", shade: "graph_primary" }
const parseStandardColorId = (standardColorId: string): { id: string; shade: string } => {
  const parts = standardColorId.split('_');
  
  if (parts.length === 2) {
    return { id: parts[0], shade: parts[1] };
  } else if (parts.length > 2) {
    // For complex IDs, assume first two parts are category, rest is shade
    return { id: parts.slice(0, 2).join('_'), shade: parts.slice(2).join('_') };
  }
  
  return { id: standardColorId, shade: '' };
};

export const EditColorPalettePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const { standardColorId } = useParams<{ standardColorId: string }>();
  const [searchParams] = useSearchParams();
  
  // Check if readonly mode (Bidgely Standard colors)
  const isReadOnly = searchParams.get('readonly') === 'true';
  
  // Read source and template params for navigation from InspectorPanel
  const source = searchParams.get('source');
  const templateKeyParam = searchParams.get('templateKey');
  const elementKeyParam = searchParams.get('elementKey');
  const localeParam = searchParams.get('locale');
  const scenarioParam = searchParams.get('scenario');
  
  // Redux selectors
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  
  const [projectLocales, setProjectLocales] = useState<string[]>(DEFAULT_LOCALES);
  
  // Derive fuelType from projectAttributes 
  const fuelType = useMemo(() => {
    let fuel = 'ELECTRIC';
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuel = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuel = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuel;
  }, [projectAttributes]);

  // State
  const hexParam = searchParams.get('hex') || '#FFFFFF';
  const [hexValue, setHexValue] = useState(hexParam);
  const [originalHexValue, setOriginalHexValue] = useState(hexParam);
  const [impactData, setImpactData] = useState<ColorImpactResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Variant state (hierarchy, locale, scenario)
  const [variant, setVariant] = useState<Variant>(DEFAULT_VARIANT);
  const [availableScenarios, setAvailableScenarios] = useState<Array<{ value: string; label: string }>>([
    { value: 'default', label: 'Default' }
  ]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  
  // Template selection
  const [selectedTemplateComponent, setSelectedTemplateComponent] = useState<{
    templateKey: string;
    componentName: string;
  } | null>(null);
  const [templateSidebarOpen, setTemplateSidebarOpen] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); 
  const headerColorPickerRef = useRef<HTMLInputElement>(null);

  // Derive channel from template key
  const channel = useMemo(() => {
    const templateKey = selectedTemplateComponent?.templateKey || '';
    if (templateKey.startsWith('WEB_') || templateKey.startsWith('DASHBOARD.')) return 'Web';
    if (templateKey === 'HER') return 'HER';
    return 'Email';
  }, [selectedTemplateComponent]);

  // Fetch scenarios when template or locale changes
  useEffect(() => {
    const templateKey = selectedTemplateComponent?.templateKey;
    
    if (!templateKey || !selectedPilotId) {
      setAvailableScenarios([{ value: 'default', label: 'Default' }]);
      return;
    }

    let ignore = false;
    async function fetchScenarios() {
      setScenariosLoading(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const dataScenariosResp = await api.getDataScenarios(
          String(selectedPilotId),
          fuelType,
          variant.locale || 'en_US'
        );

        if (ignore) return;

        const dataScenarios = dataScenariosResp?.dataScenarios as DataScenario[] | undefined;
        let scenarioOptions: Array<{ label: string; value: string }> = [];

        if (dataScenarios?.length) {
          const filteredScenarios = dataScenarios.filter((ds: DataScenario) => ds.templateName === templateKey);
          
          scenarioOptions = filteredScenarios.map((ds: DataScenario) => ({
            label: ds.scenarioDisplayName || ds.scenarioId,
            value: ds.scenarioId
          }));

          scenarioOptions.sort((a, b) => {
            const aDefault = dataScenarios.find(ds => ds.scenarioId === a.value)?.defaultScenario;
            const bDefault = dataScenarios.find(ds => ds.scenarioId === b.value)?.defaultScenario;
            if (aDefault && !bDefault) return -1;
            if (!aDefault && bDefault) return 1;
            return a.label.localeCompare(b.label);
          });
        }

        if (scenarioOptions.length === 0) {
          scenarioOptions = [{ label: 'Default', value: 'default' }];
        }

        setAvailableScenarios(scenarioOptions);
        
        if (scenarioOptions.length > 0) {
          const currentScenarioExists = scenarioOptions.some(s => s.value === variant.scenario);
          if (!currentScenarioExists) {
            setVariant(prev => ({ ...prev, scenario: scenarioOptions[0].value }));
          }
        }
        
      } catch (error) {
        console.error('[EditColorPalettePage] Failed to fetch scenarios:', error);
        setAvailableScenarios([{ value: 'default', label: 'Default' }]);
      } finally {
        if (!ignore) setScenariosLoading(false);
      }
    }

    fetchScenarios();
    setRefreshKey(prev => prev + 1);
    
    return () => { ignore = true; };
  }, [selectedTemplateComponent?.templateKey, selectedPilotId, fuelType, variant.locale, rc]);

  // Build RenderRequest for PreviewArea
  const renderRequest = useMemo<RenderRequest | null>(() => {
    if (!selectedTemplateComponent?.templateKey) {
      return null;
    }
    
    return {
      templateKey: selectedTemplateComponent.templateKey,
      elementKey: selectedTemplateComponent.componentName || 'Full Template',
      previewMode: 'desktop',
      variant: {
        hierarchy: variant.hierarchy,
        locale: variant.locale,
        scenario: variant.scenario,
      },
      refreshKey, 
    };
  }, [selectedTemplateComponent, variant, refreshKey]);

  // Handler for variant changes from sidebar
  const handleVariantChange = useCallback((newVariant: Partial<Variant>) => {
    setVariant(prev => ({ ...prev, ...newVariant }));
  }, []);

  const handleHeaderColorSwatchClick = () => {
    if (isReadOnly) return;
    headerColorPickerRef.current?.click();
  };

  const handleHeaderColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    const value = event.target.value.toUpperCase();
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setHexValue(value);
    }
  };

  const getCurrentProjectId = () => selectedPilotId || '';

  const getCurrentChannel = (): 'DIGITAL' | 'PAPER' => 'DIGITAL';

  // Fetch project locales from API
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!selectedPilotId) {
        if (!ignore) {
          setProjectLocales(DEFAULT_LOCALES);
          if (!DEFAULT_LOCALES.includes(variant.locale)) {
            setVariant(prev => ({ ...prev, locale: DEFAULT_LOCALES[0] }));
          }
        }
        return;
      }
      try {
        const resp = await rc.bffClient.getProjectLocales(String(selectedPilotId));
        const list = (resp as any)?.data?.locales || DEFAULT_LOCALES;
        if (!ignore) {
          setProjectLocales(list);
          if (!list.includes(variant.locale)) {
            setVariant(prev => ({ ...prev, locale: list[0] }));
          }
        }
      } catch {
        if (!ignore) {
          setProjectLocales(DEFAULT_LOCALES);
          if (!DEFAULT_LOCALES.includes(variant.locale)) {
            setVariant(prev => ({ ...prev, locale: DEFAULT_LOCALES[0] }));
          }
        }
      }
    })();
    return () => { ignore = true; };
  }, [selectedPilotId, rc, variant.locale]);

  // Fetch impact data on mount
  useEffect(() => {
    if (standardColorId) {
      fetchImpactData();
    }
  }, [standardColorId]);

  // Auto-select template when navigating from CXtemplateEditor (InspectorPanel)
  useEffect(() => {
    if (source === 'CXtemplateEditor' && templateKeyParam) {
      setSelectedTemplateComponent({
        templateKey: templateKeyParam,
        componentName: elementKeyParam || 'Full Template',
      });
      
      // Also set variant if provided
      if (localeParam || scenarioParam) {
        setVariant(prev => ({
          ...prev,
          locale: localeParam || prev.locale,
          scenario: scenarioParam || prev.scenario,
        }));
      }
    }
  }, [source, templateKeyParam, elementKeyParam, localeParam, scenarioParam]);

  const fetchImpactData = async () => {
    if (!standardColorId) return;

    setLoading(true);
    try {
      const { id, shade } = parseStandardColorId(standardColorId);
      const projectId = getCurrentProjectId();
      
      const response = await ColorPaletteAPI.getColorImpact(rc, {
        id,
        shade,
        projectId: projectId || undefined,
        channel: getCurrentChannel(),
        level: 'PILOT',
      });

      if (!response.success || response.error) {
        console.error('Error fetching impact data:', response.error);
        // Set empty data instead of showing error for non-critical failure
        setImpactData({
          id,
          shade,
          totalTemplates: 0,
          totalSections: 0,
          affectedTemplates: [],
        });
        return;
      }

      if (response.data) {
        setImpactData(response.data);
      }
    } catch (error) {
      console.error('Error fetching impact data:', error);
      toast.error('Failed to fetch impact data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!standardColorId || isReadOnly) return;

    if (!/^#[0-9A-F]{6}$/i.test(hexValue)) {
      toast.error('Invalid hex color format');
      return;
    }

    setSaving(true);
    try {
      const projectId = getCurrentProjectId();
      const currentChannel = getCurrentChannel();
      const { id, shade } = parseStandardColorId(standardColorId);

      const savePayload: SaveColorParams = {
        project_id: projectId || '',
        channel: currentChannel,
        id,
        shade,
        newHexValue: hexValue.toUpperCase(),
        config_level: 'pilot',
        source_name: 'color_palette_editor',
      };

      const response = await ColorPaletteAPI.saveColor(rc, savePayload);

      if (!response.success || response.error) {
        toast.error(response.error || 'Failed to save color');
        return;
      }

      const versionInfo = response.data?.versionId ? ` (Version: ${response.data.versionId})` : '';
      toast.success(`Color saved successfully${versionInfo}`);
      
      setOriginalHexValue(hexValue);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error saving color:', error);
      toast.error('Failed to save color');
    } finally {
      setSaving(false);
    }
  };

  const navigateBack = () => {
    if (source === 'CXtemplateEditor') {
      // Pass template/section state back to WysiwygPage for maintaining state across routes
      const params = new URLSearchParams();
      if (templateKeyParam) params.set('templateKey', templateKeyParam);
      if (elementKeyParam) params.set('elementKey', elementKeyParam);
      if (localeParam) params.set('locale', localeParam);
      if (scenarioParam) params.set('scenario', scenarioParam);
      
      const queryString = params.toString();
      const path = queryString 
        ? `${ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG.path}?${queryString}`
        : ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG.path;
      navigate(path, { replace: true });
    } else {
      navigate(ROUTE_TREE.CONTENT_MANAGEMENT.COLOR_PALETTE.path, { replace: true });
    }
  };

  const handleBack = () => {
    if (!isReadOnly && hexValue !== originalHexValue) {
      if (window.confirm('You have unsaved changes. Are you sure you want to go back?')) {
        navigateBack();
      }
    } else {
      navigateBack();
    }
  };

  const handleHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    let value = event.target.value.toUpperCase();
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    if (/^#[0-9A-F]{0,6}$/.test(value)) {
      setHexValue(value);
    }
  };

  const handleHexBlur = () => {
    if (!/^#[0-9A-F]{6}$/i.test(hexValue)) {
      setHexValue(originalHexValue);
    }
  };

  const handleComponentSelect = (templateKey: string, componentName: string) => {
    setSelectedTemplateComponent({ templateKey, componentName });
  };

  const getDisplayName = (colorId: string) => {
    return colorId
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const isValidHex = /^#[0-9A-F]{6}$/i.test(hexValue);
  const hasChanges = hexValue !== originalHexValue;
  const isSaveDisabled = saving || !hasChanges || !isValidHex || isReadOnly;

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!isReadOnly && (
              <input
                ref={headerColorPickerRef}
                type="color"
                value={hexValue}
                onChange={handleHeaderColorPickerChange}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: 0,
                  height: 0,
                  pointerEvents: 'none',
                }}
              />
            )}
            <Tooltip title={isReadOnly ? hexValue : "Click to open color picker"} placement="bottom">
              <Box
                onClick={handleHeaderColorSwatchClick}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: hexValue,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  cursor: isReadOnly ? 'default' : 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': isReadOnly ? {} : {
                    transform: 'scale(1.05)',
                    boxShadow: 2,
                  },
                }}
              />
            </Tooltip>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 800, 
                color: theme.palette.text.primary,
                textAlign: 'left',
              }}
            >
              {isReadOnly ? 'View' : 'Edit'} Color: {standardColorId ? getDisplayName(standardColorId) : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                textTransform: 'none',
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              }}
            >
              Back
            </Button>
            {!isReadOnly && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaveDisabled}
                sx={{
                  textTransform: 'none',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    opacity: 0.5,
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 200px)' }}>
          {/* Left Sidebar */}
          <EditColorSidebar
            hexValue={hexValue}
            onHexChange={handleHexChange}
            onHexBlur={handleHexBlur}
            templates={impactData?.affectedTemplates || []}
            loading={loading}
            onComponentSelect={handleComponentSelect}
            selectedComponent={selectedTemplateComponent}
            variant={variant}
            onVariantChange={handleVariantChange}
            availableLocales={projectLocales}
            availableScenarios={availableScenarios}
            scenariosLoading={scenariosLoading}
            isOpen={templateSidebarOpen}
            onToggleOpen={() => setTemplateSidebarOpen(!templateSidebarOpen)}
            isReadOnly={isReadOnly}
          />

          {/* Right Main Content - Template Preview */}
          <Box sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
            {renderRequest ? (
              <PreviewArea
                request={renderRequest}
                previewMode="desktop"
                channel={channel}
                isFullTemplateSelected={selectedTemplateComponent?.componentName === 'Full Template'}
                projectId={selectedPilotId}
                elementKey={selectedTemplateComponent?.componentName || 'Full Template'}
              />
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' 
                    ? theme.palette.background.paper 
                    : theme.palette.grey[50],
                }}
              >
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Select a template component to preview
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditColorPalettePage;
